import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Sprout, Lightbulb, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { initScrollAnimations } from '../utils/scrollAnimations';
import './HomePage.css';

const HomePage = () => {
  const [foundersVisible, setFoundersVisible] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [donateStep, setDonateStep] = useState('amount');
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');

  const openDonate = () => { setDonateOpen(true); setDonateStep('amount'); setSelectedAmount(100); setCustomAmount(''); setDonorName(''); setDonorEmail(''); };
  const closeDonate = () => setDonateOpen(false);
  const handleDonateNow = () => setDonateStep('details');
  const handleSubmit = () => { if (donorName && donorEmail) setDonateStep('thanks'); };

  const toggleFounders = () => {
    setFoundersVisible(!foundersVisible);
    if (!foundersVisible) {
      // Scroll to founders section after it opens
      setTimeout(() => {
        document.getElementById('founders-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Initialize scroll animations
  useEffect(() => {
    const observer = initScrollAnimations();
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-container">
          <div className="hero-ladder-left">
            <img 
              src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/gld6gvur_laddergirl.png" 
              alt="Girl climbing ladder" 
              loading="eager"
            />
          </div>
          
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="text-learn">Learn.</span>{' '}
              <span className="text-live">Live.</span>{' '}
              <span className="text-lead">Lead.</span>
            </h1>
            <h2 className="hero-subtitle-main">Building Skills for a Sustainable Future.</h2>
            <p className="hero-description">
              We go beyond English language learning by educating students about sustainability, 
              and social entrepreneurship to create a more sustainable and prosperous future.
            </p>
            <div className="hero-buttons">
              <Link to="/english" className="btn btn-hero" data-testid="hero-btn-english">
                Learn English
              </Link>
              <Link to="/programs" className="btn btn-hero" data-testid="hero-btn-programs">
                Live Sustainably
              </Link>
              <Link to="/movement" className="btn btn-hero" data-testid="hero-btn-movement">
                Lead Change
              </Link>
            </div>
          </div>

          <div className="hero-ladder-right">
            <img 
              src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/rqhv5w66_ladderboy.png" 
              alt="Boy climbing ladder" 
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Who We Serve & Who We Need Section */}
      <section className="section audience-section" style={{ backgroundColor: 'var(--light-grey)' }} data-testid="audience-section">
        <div className="content-wrapper">
          <div className="audience-grid">
            <div className="audience-card fade-in-up" data-testid="who-we-empower">
              <h2 className="audience-title">Who We Educate</h2>
              <p className="audience-description">
                We serve <strong>underprivileged youth and slum children</strong> across India, providing them with:
              </p>
              <ul className="audience-list">
                <li>Free English language education</li>
                <li>Sustainability and life skills programs</li>
                <li>Pathways to social entrepreneurship</li>
                <li>Access to mentorship and scholarships</li>
              </ul>
              <p className="audience-impact">
                Our goal: Transform lives through education and create opportunities for those who need it most.
              </p>
            </div>

            <div className="audience-card fade-in-up" data-testid="who-we-need">
              <h2 className="audience-title">Who We Work With</h2>
              <p className="audience-description">
                We connect <strong>good people for noble goals</strong> by partnering with:
              </p>
              <ul className="audience-list">
                <li>
                  <strong>Universities</strong> - Improve ranking in:<br/>
                  &nbsp;&nbsp;• Graduate Employability<br/>
                  &nbsp;&nbsp;• Employer Reputation<br/>
                  &nbsp;&nbsp;• Social Impact
                </li>
                <li>
                  <strong>University Students</strong> -<br/>
                  &nbsp;&nbsp;• Bridge the gap between your classroom learning and industry requirements<br/>
                  &nbsp;&nbsp;• Gain experience before you graduate
                </li>
                <li>
                  <strong>Professors</strong> - Be an inspiration by leading programs, mentoring students, preparing future leaders.
                </li>
              </ul>
              <p className="audience-impact">
                Together, we create a ripple effect of positive change that benefits everyone.
              </p>
            </div>
          </div>

          <div className="audience-cta">
            <Link to="/contact" className="btn btn-primary" data-testid="audience-cta">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>

      {/* Visual Relationship Section */}
      <section className="relationship-section section" data-testid="relationship-section">
        <div className="relationship-content">
          <div className="cycle-diagram-svg scale-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/bc5ou0j8_circle_4.svg" 
              alt="Learn Live Lead Cycle" 
              className="cycle-svg"
            />
          </div>
          <p className="cycle-caption">
            Knowledge becomes action, and action inspires new learning.
          </p>
        </div>
      </section>

      {/* The Litvill Pathway Section */}
      <section className="pathway-section section" style={{ backgroundColor: 'var(--light-grey)' }} data-testid="pathway-section">
        <h2 className="section-title">The Litvill Pathway</h2>
        <div className="pathway-cards">
          <div className="pathway-card card stagger-item" data-testid="pathway-card-learn">
            <div className="card-icon" style={{ color: 'var(--primary)' }}>
              <BookOpen size={48} />
            </div>
            <h3>Learn English</h3>
            <p>
              Access our <strong>Learning Management System (LMS)</strong> with thousands of self-paced 
              online lessons. Learn from peer tutors and foreign mentors through our proven 60-30-10 model.
            </p>
            <Link to="/english" className="card-link" data-testid="pathway-link-english">
              Explore <ArrowRight size={16} />
            </Link>
          </div>

          <div className="pathway-card card stagger-item" data-testid="pathway-card-live">
            <div className="card-icon" style={{ color: 'var(--accent)' }}>
              <Sprout size={48} />
            </div>
            <h3>Live Sustainably</h3>
            <p>
              Join our <strong>12-week structured programs</strong> on critical topics: Survival Skills, 
              Polycrisis, Tomorrow's Careers, Water & Food Systems, Wellness, and more.
            </p>
            <Link to="/programs" className="card-link" data-testid="pathway-link-programs">
              Discover <ArrowRight size={16} />
            </Link>
          </div>

          <div className="pathway-card card stagger-item" data-testid="pathway-card-lead">
            <div className="card-icon" style={{ color: 'var(--highlight)' }}>
              <Lightbulb size={48} />
            </div>
            <h3>Lead Change</h3>
            <p>
              Join the <strong>Social Entrepreneurship Movement</strong> - comprehensive training connecting 
              university Labs, mentors, and students to create real community impact.
            </p>
            <Link to="/movement" className="card-link" data-testid="pathway-link-movement">
              Join <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Litvill Section */}
      <section className="about-section section" style={{ backgroundColor: 'var(--light-grey)' }} data-testid="about-section">
        <h2 className="section-title">About Litvill Lessons</h2>
        <div className="about-content">
          <p className="about-text">
            Litvill Lessons is a collaborative endeavor by educators and changemakers
            connecting English learning with sustainability and social
            entrepreneurship. We empower young people to think critically, live
            consciously, and lead with purpose.
          </p>
          
          <div className="giving-back-section">
            <h3 className="giving-back-title">Giving Back to Society</h3>
            <p className="giving-back-text">
              As a non-profit organization, we are committed to creating a ripple effect of positive change. 
              Surplus funds from our initiatives directly support scholarships, enabling access to education 
              for underserved communities. By giving back, we ensure that the positive impact of our programs 
              fosters opportunities for growth and empowerment beyond individual learning.
            </p>
          </div>

          <button 
            onClick={toggleFounders}
            className="btn btn-secondary" 
            data-testid="about-founders-btn"
          >
            {foundersVisible ? (
              <>
                Hide Team <ChevronUp size={20} />
              </>
            ) : (
              <>
                Meet Our Team <ChevronDown size={20} />
              </>
            )}
          </button>
        </div>
      </section>

      {/* Team Section */}
      <section 
        id="founders-section" 
        className={`founders-section section ${foundersVisible ? 'visible' : 'hidden'}`} 
        data-testid="founders-section"
      >
        <h2 className="section-title">Meet Our Team</h2>
        <div className="founders-grid">
          <div className="founder-card" data-testid="founder-imran">
            <div className="founder-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/1jne7itn_imran2.png" 
                alt="Dr. Imran Rautan" 
                className="founder-image"
              />
            </div>
            <h3 className="founder-name">Dr. Imran Rautan</h3>
            <p className="founder-title">Founder & President</p>
            <p className="founder-bio">
              Dr. Imran Rautan rose from the slums of India to become the Founder and President of the Litvill 
              Lessons Association. Guided by his mentor, Dolores Csabai, he transformed his life through education 
              and compassion. Today, he leads a global movement that combines English learning, sustainability, 
              and social entrepreneurship to uplift underprivileged youth. His journey from poverty to purpose 
              inspires thousands to believe that true change begins within. Through Litvill, he continues to build 
              bridges between communities, creating opportunities for young minds to learn, lead, and live with 
              dignity and hope for a better tomorrow.
            </p>
          </div>

          <div className="founder-card" data-testid="founder-dolores">
            <div className="founder-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/gvrz1jmh_dolores2.png" 
                alt="Dolores Csabai" 
                className="founder-image"
              />
            </div>
            <h3 className="founder-name">Dolores Csabai</h3>
            <p className="founder-title">Chief Architect & Mentor</p>
            <p className="founder-bio">
              Dolores Csabai is the Chief Architect of the Litvill Lessons Association Learning Management System 
              and the author of several innovative school books. Known as the "Karma Engineer," she discovered 
              Dr. Imran Rautan in the slums and became his lifelong mentor and guide. With a passion for education, 
              technology, and sustainability, Dolores has dedicated her life to designing meaningful learning 
              experiences that empower underprivileged youth. As advisor and mentor to the founder, she continues 
              to shape Litvill's vision of combining English learning, sustainability, and social entrepreneurship 
              into a global movement for a more conscious and compassionate world.
            </p>
          </div>

          <div className="founder-card" data-testid="founder-stephen">
            <div className="founder-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/k1cr8b1b_stephen.png" 
                alt="Dr. Stephen Thomas Homer" 
                className="founder-image"
              />
            </div>
            <h3 className="founder-name">Dr. Stephen Thomas Homer</h3>
            <p className="founder-title">Director, Yunus Social Business Centre & Associate Professor, Sunway Business School</p>
            <p className="founder-bio">
              Dr. Stephen Thomas Homer, the former PhD supervisor of Dr. Imran Rautan, now serves as an 
              organizational mentor for Litvill Lessons. He provides strategic guidance to help channel the 
              youthful energy of the team toward its ambitious mission of empowering the next generation. 
              With his strong academic background, Dr. Stephen also supports Litvill Lessons in designing and 
              implementing robust student outcome assessments — measuring growth, impact, and learning progress 
              both before and after each program. His ongoing mentorship ensures that Litvill Lessons' work 
              remains research-informed and focused on creating measurable change.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section section" style={{ backgroundColor: 'var(--light-grey)' }} data-testid="partners-section">
        <h2 className="section-title">Partners & Collaborations</h2>
        <div className="partners-grid">
          <a href="https://presidencyuniversity.in/" target="_blank" rel="noopener noreferrer" className="partner-card stagger-item" data-testid="partner-1">
            <div className="partner-logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/0kwu45jt_presidency.png"
                alt="Presidency University"
                className="partner-logo"
              />
            </div>
            <h4>Presidency University</h4>
            <p>Bangalore</p>
            <span className="partner-tag">Signed MoU</span>
          </a>
          <a href="https://www.chitkara.edu.in/" target="_blank" rel="noopener noreferrer" className="partner-card stagger-item" data-testid="partner-2">
            <div className="partner-logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/bsgubrf6_chitkara2.png"
                alt="Chitkara University"
                className="partner-logo"
              />
            </div>
            <h4>Chitkara University</h4>
            <p>Punjab & Himachal</p>
            <span className="partner-tag">Signed MoU</span>
          </a>
          <a href="https://www.pims.org.in/" target="_blank" rel="noopener noreferrer" className="partner-card stagger-item" data-testid="partner-3">
            <div className="partner-logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/ku07q2a0_padmashree%20logo.png"
                alt="Padmashree Institute of Management & Sciences"
                className="partner-logo"
              />
            </div>
            <h4>PIMS</h4>
            <p>Bangalore</p>
            <span className="partner-tag">Signed MoU</span>
          </a>
          <a href="https://gibs.edu.in/" target="_blank" rel="noopener noreferrer" className="partner-card stagger-item" data-testid="partner-4">
            <div className="partner-logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/lztfhuuw_gibs_logo.png"
                alt="GIBS Business School"
                className="partner-logo"
              />
            </div>
            <h4>GIBS Business School</h4>
            <p>Bangalore</p>
            <span className="partner-tag">Signed MoU</span>
          </a>
          <a href="https://www.harmonyhouseindia.org/" target="_blank" rel="noopener noreferrer" className="partner-card partner-collab stagger-item" data-testid="partner-5">
            <div className="partner-logo-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/5bvfpgj2_hh.webp"
                alt="Harmony House India"
                className="partner-logo"
              />
            </div>
            <h4>Harmony House India</h4>
            <p>India</p>
            <span className="partner-tag collab">Collaboration</span>
          </a>
        </div>
        <div className="partners-cta">
          <Link to="/contact" className="btn btn-light" data-testid="partners-all-btn">
            Partner with us
          </Link>
        </div>
      </section>

      {/* Guest Speakers Section */}
      <section className="guest-speakers-section section" data-testid="guest-speakers-section">
        <div className="content-wrapper">
          <h2 className="section-title">Our Guest Speakers</h2>
          <p className="intro-text guest-speakers-intro">
            Our students are given international exposure, where international change makers contribute to our goals. 
            We collaborate with leading experts and changemakers who share their knowledge and inspire 
            our students through engaging talks and workshops on sustainability, leadership, and social impact.
          </p>
          
          <div className="speakers-grid">
            {/* Speaker 1 - Sally Bogale */}
            <div className="speaker-card stagger-item" data-testid="speaker-1">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/8xv7jszg_guest1.png"
                  alt="Sally Bogale"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Sally Bogale</h3>
                <h4 className="speaker-title">Co-Chief Executive Officer, GAIA Education</h4>
                <p className="speaker-bio">
                  Sally Bogale is the Co-CEO of Gaia Education, an award-winning international NGO that 
                  provides education for sustainable development. She empowers communities and educators 
                  worldwide to take meaningful climate action and build resilient, regenerative communities.
                </p>
              </div>
            </div>

            {/* Speaker 2 - Parasuram K */}
            <div className="speaker-card stagger-item" data-testid="speaker-2">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/es3nkvai_guest2.png"
                  alt="Parasuram K"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Parasuram K</h3>
                <h4 className="speaker-title">Corporate Social Responsibility Manager</h4>
                <p className="speaker-bio">
                  Parasuram K is a Development Management professional with 11+ years' experience in Corporate 
                  Social Responsibility, Sustainability, and ESG reporting. Currently working with Maruti Suzuki 
                  as a Manager in Corporate Affairs, he holds an MBA from Indian Institute of Forest Management.
                </p>
              </div>
            </div>

            {/* Speaker 3 - Professor Jenny Pickerill */}
            <div className="speaker-card stagger-item" data-testid="speaker-3">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/dgxymdrk_guest4.png"
                  alt="Professor Jenny Pickerill"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Professor Jenny Pickerill</h3>
                <h4 className="speaker-title">Professor of Environmental Geography, University of Sheffield, UK</h4>
                <p className="speaker-bio">
                  Professor Jenny's groundbreaking work focuses on eco-communities, environmental justice, 
                  and grassroots alternatives to mainstream development. Her insights have inspired activists, 
                  researchers, and educators worldwide, including Litvill Lessons founder Imran Rautan.
                </p>
              </div>
            </div>

            {/* Speaker 4 - Mr. Ashok Bala */}
            <div className="speaker-card stagger-item" data-testid="speaker-4">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/6nqvfo5k_guest6.png"
                  alt="Mr. Ashok Bala"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Mr. Ashok Bala</h3>
                <h4 className="speaker-title">Deputy Manager - Sustainability and Risk Governance, Rain Industries</h4>
                <p className="speaker-bio">
                  Mr. Ashok Bala is a sustainability professional with 9+ years of experience in manufacturing 
                  engineering, sustainability initiatives, and ESG reporting. He holds a post-graduate degree 
                  in Forestry Management from Indian Institute of Forest Management, Bhopal.
                </p>
              </div>
            </div>

            {/* Speaker 5 - Professor Sedlacek Sabine */}
            <div className="speaker-card stagger-item" data-testid="speaker-5">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/ixcgop2a_guest5.png"
                  alt="Professor Sedlacek Sabine"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Professor Sedlacek Sabine</h3>
                <h4 className="speaker-title">Head of the School of Sustainability, Governance, and Methods, Modul University, Vienna, Austria</h4>
                <p className="speaker-bio">
                  Professor Sabine Sedlacek is Head of the School of Sustainability, Governance, and Methods 
                  at Modul University, Vienna. Her research focuses on urban and regional governance, 
                  sustainability transitions, and the role of stakeholders in fostering resilient communities.
                </p>
              </div>
            </div>

            {/* Speaker 6 - Sqn Ldr (Dr.) Rina Angel */}
            <div className="speaker-card stagger-item" data-testid="speaker-6">
              <div className="speaker-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/4fss3zyk_guest3.png"
                  alt="Sqn Ldr (Dr.) Rina Angel"
                  className="speaker-image"
                />
              </div>
              <div className="speaker-info">
                <h3 className="speaker-name">Sqn Ldr (Dr.) Rina Angel</h3>
                <h4 className="speaker-title">Vice President - Office of Infrastructure Enhancement, Chairperson - Centre of Excellence of Sustainability, CHITKARA University, Punjab</h4>
                <p className="speaker-bio">
                  Commissioned into the Indian Air Force in 1996, Dr. Rina Angel now serves at Chitkara University 
                  as an ardent sustainability advocate. She has pioneered innovative campus solutions including 
                  accessible transportation and waste management systems, applying the 7 Rs of Sustainability 
                  while championing the UN principle of "leave no one behind."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="video-section section" data-testid="video-section">
        <h2 className="section-title">See Litvill in Action</h2>
        <p className="video-description">
          Discover how we're transforming lives through English learning, sustainability, and social entrepreneurship.
        </p>
        <div className="video-container">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/Kny5c7sglA4"
            title="Litvill Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="litvill-video"
          ></iframe>
        </div>
      </section>

      {/* Join the Movement Section */}
      <section className="join-section section" data-testid="join-section">
        <div className="join-content">
          <div className="join-image">
            <img
              src="https://images.unsplash.com/photo-1652305500057-0fcb348b62aa"
              alt="Students collaborating"
              loading="lazy"
            />
          </div>
          <div className="join-text">
            <h2>Join the Movement</h2>
            <p>
              Whether you're a student, mentor, or university, join us in shaping
              the next generation of changemakers.
            </p>
            <div className="join-buttons">
              <Link to="/contact" className="btn btn-primary" data-testid="join-involved-btn">
                Get Involved
              </Link>
              <button
                onClick={openDonate}
                className="btn btn-secondary"
                data-testid="donate-btn"
              >
                Support Our Cause
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Modal */}
      {donateOpen && (
        <div className="donate-overlay" onClick={closeDonate}>
          <div className="donate-modal" onClick={e => e.stopPropagation()}>
            <button className="donate-close" onClick={closeDonate}>✕</button>

            {donateStep === 'amount' && (
              <>
                <h2 className="donate-title">Support Our Cause</h2>
                <p className="donate-subtitle">How much would you like to donate today?</p>
                <p className="donate-note">All donations directly impact our students and help us further our mission.</p>
                <div className="donate-amounts">
                  {[10, 25, 50, 100, 250, 500].map(amt => (
                    <button
                      key={amt}
                      className={`donate-amount-btn ${selectedAmount === amt && !customAmount ? 'active' : ''}`}
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  className="donate-custom"
                  placeholder="Enter custom amount"
                  value={customAmount}
                  onChange={e => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                />
                <p className="donate-nonrefundable">⚠️ Please note: All donations are non-refundable.</p>
                <button className="donate-now-btn" onClick={handleDonateNow}>
                  Donate now →
                </button>
              </>
            )}

            {donateStep === 'details' && (
              <>
                <h2 className="donate-title">Almost there!</h2>
                <p className="donate-subtitle">You're donating ₹{customAmount || selectedAmount}. Please leave your details:</p>
                <input
                  type="text"
                  className="donate-input"
                  placeholder="Your name *"
                  value={donorName}
                  onChange={e => setDonorName(e.target.value)}
                />
                <input
                  type="email"
                  className="donate-input"
                  placeholder="Your email *"
                  value={donorEmail}
                  onChange={e => setDonorEmail(e.target.value)}
                />
                <p className="donate-nonrefundable">⚠️ Please note: All donations are non-refundable.</p>
                <button className="donate-now-btn" onClick={handleSubmit} disabled={!donorName || !donorEmail}>
                  Submit
                </button>
              </>
            )}

            {donateStep === 'thanks' && (
              <div className="donate-thanks">
                <div className="donate-thanks-icon">🙏</div>
                <h2 className="donate-title">Thank You, {donorName}!</h2>
                <p className="donate-subtitle">Your interest in supporting Litvill Lessons means the world to us. We'll be in touch at {donorEmail} with next steps.</p>
                <button className="donate-now-btn" onClick={closeDonate}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;